import {
  buildBoundary,
  centreForBoundary,
} from "./engine_parts/gravity/index.js";
import { applyRadialEdgeCentreStep } from "./engine_parts/gravity/applyRadialGravityStep.js";

function toNum(v, d = 0) { const n = Number(v); return Number.isFinite(n) ? n : d; }
function bool(v) { return !!v; }

export default class GravityManager {
  constructor() {
    this._grav = {
      enabled: false,
      speed: 0,       // m/s (signed; UI: negative=inward, positive=outward)
      lingerS: 0,     // optional linger at stop (centre or edge/random stop)
    };

    this._ctx = {
      emitterShape: "dot",
      shapeSettings: {},
      origin: { x: 0, y: 0 },
      pxPerMeter: 80,
      imageRect: { x: 0, y: 0, width: 0, height: 0 },
    };

    this._boundary = null;
    this._centre = { x: 0, y: 0 };
    this._sig = "";
  }

  /* ------------------------ Settings API ------------------------ */
  setEnabled(on) { this._grav.enabled = bool(on); }
  setSpeed(mps)  { this._grav.speed   = toNum(mps, 0); }
  setLinger(s)   { this._grav.lingerS = Math.max(0, toNum(s, 0)); }

  updateSettings(patch = {}) {
    if (Object.prototype.hasOwnProperty.call(patch, "gravityEnabled")) this._grav.enabled = !!patch.gravityEnabled;
    if (Object.prototype.hasOwnProperty.call(patch, "gravitySpeed"))   this._grav.speed   = toNum(patch.gravitySpeed, this._grav.speed);
    if (Object.prototype.hasOwnProperty.call(patch, "gravityLingerS")) this._grav.lingerS = Math.max(0, toNum(patch.gravityLingerS, this._grav.lingerS));
  }

  isEnabled() { return !!this._grav.enabled; }
  getSettings() { return { ...this._grav }; }

  /* ------------------- Geometry/Context ------------------- */
  configure({ emitterShape, shapeSettings, origin, pxPerMeter, imageRect } = {}) {
    const emShape = emitterShape || this._ctx.emitterShape || "dot";
    const ss = shapeSettings || this._ctx.shapeSettings || {};
    const org = origin || this._ctx.origin || { x: 0, y: 0 };
    const ppm = Number.isFinite(pxPerMeter) ? pxPerMeter : (this._ctx.pxPerMeter || 80);
    const img = imageRect || this._ctx.imageRect || { x: 0, y: 0, width: 0, height: 0 };

    this._ctx = { emitterShape: emShape, shapeSettings: ss, origin: org, pxPerMeter: ppm, imageRect: img };

    const sig = JSON.stringify({
      emShape,
      ss,
      orgX: org?.x | 0,
      orgY: org?.y | 0,
      ppm: Math.round(ppm * 1000) / 1000,
      imgW: img?.width | 0,
      imgH: img?.height | 0,
    });

    if (sig !== this._sig) {
      this._sig = sig;
      this._boundary = buildBoundary({
        emitterShape: emShape,
        shapeSettings: ss,
        origin: org,
        pxPerMeter: ppm,
        imageRect: img,
      });
      this._centre = centreForBoundary(this._boundary);
    }
  }

  /* ------------------------------ Step ---------------------------------- */
  update(particles, dt) {
    if (!this._grav.enabled) return 0;

    if (!this._boundary) {
      this._boundary = buildBoundary({
        emitterShape: this._ctx.emitterShape,
        shapeSettings: this._ctx.shapeSettings,
        origin: this._ctx.origin,
        pxPerMeter: this._ctx.pxPerMeter,
        imageRect: this._ctx.imageRect,
      });
      this._centre = centreForBoundary(this._boundary);
    }

    const ctx = {
      boundary: this._boundary,
      centre: this._centre,
      effectSettings: {
        gravityEnabled: true,
        gravitySpeed: this._grav.speed,
        gravityLingerS: this._grav.lingerS,
      },
      pxPerMeter: this._ctx.pxPerMeter,
      imageRect: this._ctx.imageRect,
    };

    let handled = 0;
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];

      // Do the positional gravity step (also handles linger-at-stop policy)
      const did = applyRadialEdgeCentreStep(p, dt, ctx);
      if (did) {
        handled++;
        p.__gravMoved = true;

        // While gravity is in control, keep fully opaque (visual policy)
        if (p && p.s && typeof p.s.alpha === "number") {
          p.s.alpha = 1;
        }
        if (typeof p.alpha === "number") p.alpha = 1;
      }
    }
    return handled;
  }
}
